import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentManagementService } from 'src/app/Services/content-management/content-management.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WeeklyPracticeTestStudyUnitSelectionComponent } from '../weekly-practice-test-study-unit-selection/weekly-practice-test-study-unit-selection.component';
import { ExamManagementService } from 'src/app/Services/exam-management/exam-management.service';
import { ModalService } from 'src/app/Services/confirmation-modal/modalServie.service';
import { ConstantVars } from 'src/app/utils/constants/constants';
import { ToastrService } from 'ngx-toastr';

/* ================= INTERFACES ================= */

export interface AnswerChoice {
  Answer_Choice_Sequence_Number?: number;
  Answer_Choice_Id: number;
  Answer_Choice_Text: string;
  Right_Answer_Ind: string;
  All_Of_The_Above_Or_None_Of_The_Above_Ind: string;
  Selected_IND: string;
  selected?: boolean;
}

export interface Question {
  Path_Name: string;
  Question_ID: string;
  Question_Text: string;
  Answer_Choices: AnswerChoice[];
}

export interface ISubjectWithQuestions {
  Subject_Id: number;
  Subject_Name: string;
  Subject_Total_Number_Of_Question: number;
  Subject_Current_Number_Of_Question: number;
  Total_Number_Of_Question: number;
  Current_Number_Of_Question: number;
  isCollapsed: boolean;
  questions: Question[];
}

/* ================= COMPONENT ================= */

@Component({
  selector: 'app-weekly-practice-test-questions',
  templateUrl: './weekly-practice-test-questions.component.html',
  styleUrls: ['./weekly-practice-test-questions.component.css']
})
export class WeeklyPracticeTestQuestionsComponent implements OnInit, OnDestroy {

  courseId = '';
  weeklyPracticeTestId!: number;
  subjectIdFromRoute: number | null = null;
  subjectLocked = false;

  practiceTestSequenceNumber!: number;
  practiceTestName = '';
  ExamAgencyName = '';
  CourseName = '';
  ExamYear = '';
  totalQuestions!: number;
  subjectName = '';

  subjectWithQuestions: ISubjectWithQuestions[] = [];

  subjectList: any[] = [];
  selectedSubject = 0;

  private destroy$ = new Subject<void>();

  constructor(
    public contentService: ContentManagementService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private examService: ExamManagementService,
    private confirmationModalService: ModalService,
    private readonly toaster: ToastrService
  ) {}

  /* ================= LIFECYCLE ================= */

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.weeklyPracticeTestId = +params['id'];
        this.tryLoadSelectedUnits();
      });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(queryParams => {
        this.courseId = queryParams['c_id'];
        this.subjectIdFromRoute = queryParams['subject_id']
          ? +queryParams['subject_id']
          : null;

        this.practiceTestSequenceNumber = +queryParams['p_Seq'];
        this.practiceTestName = queryParams['p_Name'] || '';
        this.ExamAgencyName = queryParams['eA_Name'];
        this.CourseName = queryParams['c_Name'];
        this.ExamYear = queryParams['eY_Name'];
        this.totalQuestions = +queryParams['t_q'];
        this.subjectName = queryParams['s_name'];

        if (this.courseId) {
          this.loadSubjects();
        }
      });
  }

  /* ================= SUBJECTS ================= */

  loadSubjects(): void {
    const payload = {
      currentNodeID: 0,
      screenName: 'STUDY UNIT',
      childRecordStatus: 'ALL',
      CalledFrom: `STUDY UNIT@${this.courseId}`,
      sessionLanguageTypeCode: 'ENG'
    };

    this.contentService
      .getSubjectTree(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        res => {
          this.subjectList = res || [];
          console.log('🔥 Subjects loaded:', this.subjectList);
        },
        error => {
          console.error('❌ Error loading subjects:', error);
          this.toaster.error('Error loading subjects');
        }
      );
  }

  SelectSubject(event: any): void {
    this.selectedSubject = +event.target.value;

    const modalRef = this.modalService.open(
      WeeklyPracticeTestStudyUnitSelectionComponent,
      { size: 'lg', backdrop: 'static', keyboard: false }
    );

    const selectedSubjectName =
      this.subjectList.find(s => s.ID === this.selectedSubject)?.Name || '';

    modalRef.componentInstance.SubjectId = this.selectedSubject;
    modalRef.componentInstance.courseId = +this.courseId;
    modalRef.componentInstance.weeklyPracticeTestId = this.weeklyPracticeTestId;
    modalRef.componentInstance.practiceTestSequenceNumber = this.practiceTestSequenceNumber;
    modalRef.componentInstance.ExamAgencyName = this.ExamAgencyName;
    modalRef.componentInstance.CourseName = this.CourseName;
    modalRef.componentInstance.ExamYear = this.ExamYear;
    modalRef.componentInstance.totalQuestions = this.totalQuestions;
    modalRef.componentInstance.subjectName = selectedSubjectName;

    modalRef.result.finally(() => this.tryLoadSelectedUnits());
  }

  navigateToStudyUnitSelection(): void {
    const modalRef = this.modalService.open(
      WeeklyPracticeTestStudyUnitSelectionComponent,
      { size: 'lg', backdrop: 'static', keyboard: false }
    );

    const selectedSubjectName =
      this.subjectList.find(s => s.ID === this.selectedSubject)?.Name || '';

    modalRef.componentInstance.SubjectId = this.selectedSubject;
    modalRef.componentInstance.courseId = +this.courseId;
    modalRef.componentInstance.weeklyPracticeTestId = this.weeklyPracticeTestId;
    modalRef.componentInstance.practiceTestSequenceNumber = this.practiceTestSequenceNumber;
    modalRef.componentInstance.ExamAgencyName = this.ExamAgencyName;
    modalRef.componentInstance.CourseName = this.CourseName;
    modalRef.componentInstance.ExamYear = this.ExamYear;
    modalRef.componentInstance.totalQuestions = this.totalQuestions;
    modalRef.componentInstance.subjectName = selectedSubjectName;

    modalRef.result.finally(() => this.tryLoadSelectedUnits());
  }

  /* ================= QUESTIONS ================= */

  tryLoadSelectedUnits(): void {
    if (!this.weeklyPracticeTestId) return;

    const payload = { CourseTestID: this.weeklyPracticeTestId };

    this.examService
      .getWeeklyPracticeTestStudyUnit(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any[]) => {
        const hasSelected = res?.some(item => item.Selected_Ind === 'Y');

        if (hasSelected && this.subjectIdFromRoute) {
          this.selectedSubject = this.subjectIdFromRoute;
          this.subjectLocked = true;
          this.getPracticeTestQuestion();
        } else {
          this.subjectLocked = false;
        }
      });
  }

  getPracticeTestQuestion(): void {
    const payload = {
      CourseTestId: this.weeklyPracticeTestId,
      CourseTestStatusTypeCode: null
    };

    this.examService
      .getWeeklyPracticeTestQuestion(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: ISubjectWithQuestions[]) => {

        this.subjectWithQuestions = (res || []).map(subject => ({
          ...subject,
          isCollapsed: true,
          questions: subject.questions.map(question => ({
            ...question,
            Answer_Choices: question.Answer_Choices.map(choice => ({
              ...choice,
              selected: choice.Selected_IND === 'Y'
            }))
          }))
        }));
      });
  }

  /* ================= ACTIONS ================= */

   async onDelete(subject:ISubjectWithQuestions,question: Question) {
      try {
        const result = await this.confirmationModalService.open(
          'Confirmation',
          `Do you want to remove this Question?`
        );

        if (result) {
          // this.subjectIdOpen = subject.Subject_Id;
          const payload = {
            UDTTestQuestion: [
              {
                CourseTestId: this.weeklyPracticeTestId,
                QuestionId: question.Question_ID,
                QuestionNumber: 0,
                RightAnswerScore: 0,
                WrongAnswerScore: 0,
                TimeToAnswerInSeconds: 0,
                OptionalQuestionInd: 'N',
                SelectedIND: 'N',
              },
            ],

            UDTTestQuestionAnswerChoice: question.Answer_Choices.map(
              (choice) => ({
                CourseTestId: this.weeklyPracticeTestId,
                QuestionId: question.Question_ID,
                AnswerChoiceId: choice.Answer_Choice_Id, // Ensure number type
                AnswerChoiceSequenceNumber: 0,
                SelectedIND: 'N',
              })
            ),
          };

          this.examService.insertOrUpdateTestQuestion(payload).subscribe(
            (res) => {
              console.log(res);
              this.toaster.success('Questions Deleted successfully', '', {
                timeOut: ConstantVars.ToastSucessTimeout,
              });
              this.getPracticeTestQuestion();
            },
            (error) => {
              console.log(error);

              this.toaster.error(error.error.Message, '', {
                timeOut: ConstantVars.ToastErrorTimeout,
              });
            }
          );
        }
      } catch (error) {
        console.log(error);
      }
    }

  onEdit(subject: ISubjectWithQuestions, question: Question): void {
    this.router.navigate(['Exam/WeeklyPracticeTestQuestionSelection', this.weeklyPracticeTestId], {
      queryParams: {
        c_id: this.courseId,
        p_Seq: this.practiceTestSequenceNumber,
        p_Name: this.practiceTestName,
        eA_Name: this.ExamAgencyName,
        c_Name: this.CourseName,
        eY_Name: this.ExamYear,
        s_id: subject.Subject_Id,
        t_q: subject.Subject_Total_Number_Of_Question,
        s_name: subject.Subject_Name,
        q_id: question.Question_ID,
      },
    });
  }

  /* ================= DESTROY ================= */

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
